import type { HostMessage, WorkerLike, WorkerMessage } from './protocol'

export type SupervisorOptions = {
  readonly createWorker: () => WorkerLike
  /** Only messages from the live worker reach this; a terminated worker is muted. */
  readonly onMessage: (message: WorkerMessage) => void
  readonly onError: () => void
}

export type WorkerSupervisor = {
  /** Posts to the live worker, spawning one if needed. */
  readonly post: (message: HostMessage) => void
  readonly alive: () => boolean
  readonly terminate: () => void
}

/** Owns the worker handle: spawn on demand, terminate, and mute dead workers. */
export const createWorkerSupervisor = (options: SupervisorOptions): WorkerSupervisor => {
  let worker: WorkerLike | null = null

  const spawn = (): WorkerLike => {
    const w = options.createWorker()
    w.onmessage = (event) => {
      if (w === worker) options.onMessage(event.data)
    }
    w.onerror = () => {
      if (w === worker) options.onError()
    }
    worker = w
    return w
  }

  return {
    post: (message) => (worker ?? spawn()).postMessage(message),
    alive: () => worker !== null,
    terminate: () => {
      worker?.terminate()
      worker = null
    },
  }
}
